import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Button,
    Box,
    Chip,
} from '@mui/material';
import styles from '../OrderList.module.css';

function OrderTable({
    OrderD,
    setmodalData,
    setOpenModal,
    setRefresh,
    setPrintModalData,
    setOpenPrint,
    selectedItems,
    handleToggleItem,
    handleToggleAll,
    selectAll,
    navigate,
    AlartSubmit,
    loading,
    editPath = '/edit-order',
    callInfo = { enabled: false, status: {} },
    onAiCall,
    onSoftphoneCall,
    onOpenCall,
}) {
    // the browser softphone (mounted once in App) tells whether it can dial
    const [softphone, setSoftphone] = useState(() => !!window.__etechSoftphone?.enabled);
    const [menuFor, setMenuFor] = useState(null);
    useEffect(() => {
        const on = (e) => setSoftphone(!!e.detail?.enabled);
        window.addEventListener('etech-softphone-status', on);
        return () => window.removeEventListener('etech-softphone-status', on);
    }, []);
    useEffect(() => {
        if (!menuFor) return undefined;
        const close = () => setMenuFor(null);
        window.addEventListener('click', close);
        window.addEventListener('scroll', close, true);
        return () => {
            window.removeEventListener('click', close);
            window.removeEventListener('scroll', close, true);
        };
    }, [menuFor]);
    /**
     * Order Status Display Map
     * Maps all 13 status codes to display text and colors
     * See backend models/order.js for complete status reference
     */
    const getOrderStatusInfo = (status) => {
        const statusMap = {
            1: { text: 'New Order', color: '#3b82f6', bgColor: '#dbeafe' },
            2: { text: 'Call Done', color: '#059669', bgColor: '#d1fae5' },
            3: { text: 'Courier Done', color: '#7c3aed', bgColor: '#ede9fe' },
            4: { text: 'Print Done', color: '#dc2626', bgColor: '#fee2e2' },
            5: { text: 'Cancel', color: '#6b7280', bgColor: '#f3f4f6' },
            6: { text: 'Return', color: '#ea580c', bgColor: '#fed7aa' },
            7: {
                text: 'Call Not Received',
                color: '#dc2626',
                bgColor: '#fee2e2',
            },
            8: { text: 'Ask Advance', color: '#d97706', bgColor: '#fef3c7' },
            9: { text: 'Ship Later', color: '#0891b2', bgColor: '#cffafe' },
            10: { text: 'Waiting', color: '#7c2d12', bgColor: '#fed7aa' },
            11: { text: 'Overseas', color: '#1e40af', bgColor: '#dbeafe' },
            12: {
                text: 'Delivery Issue',
                color: '#dc2626',
                bgColor: '#fee2e2',
            },
            13: {
                text: 'Payment Received',
                color: '#059669',
                bgColor: '#d1fae5',
            },
        };
        return (
            statusMap[status] || {
                text: 'Unknown',
                color: '#6b7280',
                bgColor: '#f3f4f6',
            }
        );
    };

    // Limit name to max 13 characters
    const formatName = (name) => {
        if (!name) return 'N/A';
        const maxChars = 13;
        return name.length > maxChars ? name.slice(0, maxChars) + '..' : name;
    };

    // Handle cell click (opens modal, etc.)
    const handleCellClick = (item) => {
        setmodalData(item);
        setOpenModal(true);
        setRefresh(false);
    };

    // If loading, show skeleton rows
    if (loading) {
        return (
            <div className={styles['shopify-table-container']}>
                <div className={styles['skeleton-table-inline']}>
                    <div className={styles['skeleton-header-row']}>
                        <div className={`${styles.skeleton}`} style={{ width: 18, height: 18, borderRadius: 4 }}></div>
                        <div className={`${styles.skeleton}`} style={{ width: 80, height: 12 }}></div>
                        <div className={`${styles.skeleton}`} style={{ width: 60, height: 12 }}></div>
                        <div className={`${styles.skeleton}`} style={{ width: 50, height: 12 }}></div>
                        <div className={`${styles.skeleton}`} style={{ width: 80, height: 12 }}></div>
                        <div className={`${styles.skeleton}`} style={{ width: 60, height: 12 }}></div>
                        <div className={`${styles.skeleton}`} style={{ width: 70, height: 12 }}></div>
                    </div>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <div key={n} className={styles['skeleton-row']}>
                            <div className={`${styles.skeleton} ${styles['skeleton-checkbox']}`}></div>
                            <div className={styles['skeleton-customer']}>
                                <div className={`${styles.skeleton} ${styles['skeleton-name']}`}></div>
                                <div className={`${styles.skeleton} ${styles['skeleton-phone']}`}></div>
                            </div>
                            <div className={`${styles.skeleton} ${styles['skeleton-amount']}`}></div>
                            <div className={`${styles.skeleton} ${styles['skeleton-items']}`}></div>
                            <div className={`${styles.skeleton} ${styles['skeleton-assigned']}`}></div>
                            <div className={`${styles.skeleton} ${styles['skeleton-status']}`}></div>
                            <div className={`${styles.skeleton} ${styles['skeleton-actions']}`}></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // If not loading and no data, show "No Data Found"
    // Add safety check for undefined OrderD
    if (!OrderD || !Array.isArray(OrderD) || OrderD.length === 0) {
        return (
            <div
                style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#888',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '150px',
                }}
            >
                No Data Found
            </div>
        );
    }

    // Define columns for header and cells
    const columns = [
        {
            id: 'name',
            label: 'Customer',
            width: 200,
            align: 'left',
            render: (item) => (
                <div className={styles['customer-cell']}>
                    <Checkbox
                        checked={selectedItems.includes(item)}
                        onChange={() => handleToggleItem(item)}
                        size="small"
                        sx={{ padding: '4px' }}
                    />
                    <div className={styles['customer-info']}>
                        <div className={styles['customer-name']}>
                            {formatName(item.cName)}
                        </div>
                        <div className={styles['customer-mobile']}>
                            {item.cMobile}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: 'amount',
            label: 'Amount',
            width: 100,
            align: 'right',
            render: (item) => (
                <div
                    className={styles['amount-cell']}
                    onClick={() => handleCellClick(item)}
                >
                    <div className={styles['amount-value']}>
                        ৳{item.totalPrice}
                    </div>
                </div>
            ),
        },
        {
            id: 'item',
            label: 'Items',
            width: 60,
            align: 'center',
            render: (item) => (
                <div
                    className={styles['item-cell']}
                    onClick={() => handleCellClick(item)}
                >
                    <span className={styles['item-count']}>{item.item}</span>
                </div>
            ),
        },
        {
            id: 'assigned',
            label: 'Assigned To',
            width: 120,
            align: 'left',
            render: (item) => (
                <div
                    className={styles['assigned-cell']}
                    onClick={() => handleCellClick(item)}
                >
                    <span className={styles['assigned-name']}>
                        {item.assignedName || 'Admin'}
                    </span>
                </div>
            ),
        },
        {
            id: 'status',
            label: 'Status',
            width: 140,
            align: 'center',
            render: (item) => {
                const statusInfo = getOrderStatusInfo(item.orderStatus);
                return (
                    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Chip
                            label={statusInfo.text}
                            size="small"
                            sx={{
                                backgroundColor: statusInfo.bgColor,
                                color: statusInfo.color,
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: statusInfo.bgColor,
                                    opacity: 0.8,
                                },
                            }}
                            onClick={() => handleCellClick(item)}
                        />
                        <SteadfastBadge sf={item.SteadFastConsignment} />
                    </div>
                );
            },
        },
        ...(callInfo.enabled
            ? [
                  {
                      id: 'call',
                      label: 'Call',
                      width: 130,
                      align: 'center',
                      render: (item) => <CallCell item={item} info={callInfo.status[item._id]} menu={menuFor && menuFor.id === item._id ? menuFor : null} setMenu={(m) => setMenuFor(m ? { id: item._id, ...m } : null)} softphone={softphone} onAiCall={onAiCall} onSoftphoneCall={onSoftphoneCall} onOpenCall={onOpenCall} />,
                  },
              ]
            : []),
        {
            id: 'actions',
            label: 'Actions',
            width: 200,
            align: 'right',
            render: (item) => (
                <div className={styles['actions-cell']}>
                    {editPath && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(editPath, { state: item })}
                        sx={{
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                            marginRight: '4px',
                            textTransform: 'none',
                        }}
                    >
                        Edit
                    </Button>
                    )}
                    {AlartSubmit && (
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => AlartSubmit(item._id)}
                        sx={{
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                            marginRight: '4px',
                            textTransform: 'none',
                        }}
                    >
                        Delete
                    </Button>
                    )}
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setPrintModalData(item);
                            setOpenPrint(true);
                            setRefresh(false);
                        }}
                        sx={{
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                            textTransform: 'none',
                        }}
                    >
                        Print
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div className={styles['shopify-table-container']}>
            <TableContainer
                component={Paper}
                className={styles['shopify-table-wrapper']}
            >
                <Table
                    className={styles['shopify-table']}
                    size="small"
                    aria-label="orders table"
                >
                    <TableHead className={styles['table-header']}>
                        <TableRow className={styles['header-row']}>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    align={col.align}
                                    className={styles['header-cell']}
                                    sx={{ width: col.width }}
                                >
                                    {col.id === 'name' ? (
                                        <div
                                            className={
                                                styles['header-with-checkbox']
                                            }
                                        >
                                            <Checkbox
                                                checked={selectAll}
                                                onChange={handleToggleAll}
                                                size="small"
                                                sx={{ padding: '4px' }}
                                            />
                                            <span>{col.label}</span>
                                        </div>
                                    ) : (
                                        <span className={styles['header-text']}>
                                            {col.label}
                                        </span>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {OrderD.map((item, index) => (
                            <TableRow
                                key={item._id}
                                className={styles['table-row']}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: '#f6f6f7',
                                    },
                                }}
                            >
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        align={col.align}
                                        className={styles['table-cell']}
                                        sx={{ width: col.width }}
                                    >
                                        {col.render(item)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

// Steadfast parcel state (booked here, or found by invoice number by the hourly sync).
const SF_STATUS = {
    in_review: ['In review', '#4b5563', '#f1f2f3'],
    pending: ['On the way', '#0e7490', '#cffafe'],
    hold: ['On hold', '#92400e', '#fef3c7'],
    delivered: ['Delivered', '#065f46', '#d1fae5'],
    partial_delivered: ['Part delivered', '#065f46', '#d1fae5'],
    delivered_approval_pending: ['Delivered (pending)', '#065f46', '#ecfdf5'],
    partial_delivered_approval_pending: ['Part delivered (pending)', '#065f46', '#ecfdf5'],
    cancelled: ['Returning', '#991b1b', '#fee2e2'],
    cancelled_approval_pending: ['Cancel (pending)', '#991b1b', '#fef2f2'],
    unknown: ['Unknown', '#4b5563', '#f1f2f3'],
    unknown_approval_pending: ['Unknown (pending)', '#4b5563', '#f1f2f3'],
};
function SteadfastBadge({ sf }) {
    if (!sf || !sf.status || sf.status === 'not_found') return null;
    const s = SF_STATUS[sf.status] || [sf.status, '#4b5563', '#f1f2f3'];
    const style = { borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600, color: s[1], background: s[2], textDecoration: 'none', whiteSpace: 'nowrap' };
    const title = `Steadfast: ${s[0]}${sf.checkedAt ? ` (checked ${new Date(sf.checkedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''}`;
    return sf.tracking_code ? (
        <a href={`https://steadfast.com.bd/t/${encodeURIComponent(sf.tracking_code)}`} target="_blank" rel="noreferrer" title={title} style={style} onClick={(e) => e.stopPropagation()}>
            🚚 {s[0]}
        </a>
    ) : (
        <span title={title} style={style}>
            🚚 {s[0]}
        </span>
    );
}

const CALL_RESULT = {
    confirmed: ['AI: confirmed', '#065f46', '#d1fae5'],
    cancelled: ['AI: cancelled', '#991b1b', '#fee2e2'],
    later: ['AI: later', '#0e7490', '#cffafe'],
    change: ['AI: change', '#92400e', '#fef3c7'],
};
const CALL_OUTCOME = { no_answer: 'No answer', busy: 'Busy', rejected: 'Rejected', failed: 'Failed', completed: 'Talked', callback: 'Call back' };

/** The order's call button (AI confirm / softphone) and its last call. The menu is a portal (the table scrolls and would clip it). */
function CallCell({ item, info, menu, setMenu, softphone, onAiCall, onSoftphoneCall, onOpenCall }) {
    const last = info?.last;
    const r = last && CALL_RESULT[last.result];
    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (menu) return setMenu(null);
                    const b = e.currentTarget.getBoundingClientRect();
                    setMenu({ top: Math.min(b.bottom + 4, window.innerHeight - 100), left: Math.max(8, Math.min(b.left + b.width / 2 - 95, window.innerWidth - 198)) });
                }}
                title="Call this customer"
                style={{ border: '1px solid #c9cccf', background: '#fff', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 13 }}
            >
                📞 Call
            </button>
            {menu &&
                createPortal(
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'fixed', top: menu.top, left: menu.left, zIndex: 1400, background: '#fff', border: '1px solid #e1e3e5', borderRadius: 8, boxShadow: '0 8px 20px rgba(0,0,0,0.12)', width: 190, textAlign: 'left', overflow: 'hidden' }}
                    >
                        <button type="button" style={menuItem} onClick={() => { setMenu(null); onAiCall(item); }}>
                            🤖 AI confirm call
                        </button>
                        {softphone && (
                            <button type="button" style={menuItem} onClick={() => { setMenu(null); onSoftphoneCall(item); }}>
                                🎧 Call with softphone
                            </button>
                        )}
                    </div>,
                    document.body
                )}
            {info?.waiting ? (
                <span style={{ fontSize: 11, color: '#6d7175', textAlign: 'center' }}>
                    {info.waiting === 'calling'
                        ? 'AI calling now…'
                        : info.at && new Date(info.at).getTime() > Date.now() + 60000
                          ? `AI call ${new Date(info.at).toDateString() === new Date().toDateString() ? 'at' : 'on'} ${new Date(info.at).toLocaleString('en-GB', new Date(info.at).toDateString() === new Date().toDateString() ? { hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                          : 'AI call queued'}
                    {info.tries > 0 && info.maxTries ? ` · try ${Math.min(info.tries + 1, info.maxTries)}/${info.maxTries}` : ''}
                </span>
            ) : info?.unreached && item.orderStatus === 1 ? (
                <button
                    type="button"
                    onClick={() => last && onOpenCall(last.callId)}
                    title="Every AI call went unanswered — call the customer yourself"
                    style={{ border: 'none', cursor: last ? 'pointer' : 'default', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600, color: '#8e1f0b', background: '#fff4f4' }}
                >
                    ⚠ AI couldn't reach{info.tries ? ` · ${info.tries} call${info.tries === 1 ? '' : 's'}` : ''}
                </button>
            ) : last ? (
                <button
                    type="button"
                    onClick={() => onOpenCall(last.callId)}
                    title="See the call"
                    style={{ border: 'none', cursor: 'pointer', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600, color: r ? r[1] : '#4b5563', background: r ? r[2] : '#f1f2f3' }}
                >
                    {r ? r[0] : last.kind === 'softphone' ? `Softphone: ${CALL_OUTCOME[last.outcome] || 'call'}` : `AI: ${CALL_OUTCOME[last.outcome] || last.state}`}
                </button>
            ) : null}
        </div>
    );
}

const menuItem = { display: 'block', width: '100%', border: 'none', background: '#fff', padding: '9px 12px', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: '#202223' };

export default OrderTable;
